import { css } from "@emotion/react";
import { appColor, appTypo, CustomMuiButtonBlue } from "@unit/styles";

/** Agency auth page design tokens (Figma) */
export const authColors = {
  title: "#081735",
  body: "#445470",
  inputBg: "#F8FAFB",
  inputBorder: "#DEE3EF",
} as const;

export const authFontSizes = {
  title: "24px",
  subtitle: "16px",
  label: "14px",
  link: "18px",
  footer: "16px",
  button: "16px",
} as const;

export const authPageContainer = css`
  min-height: 100vh;
  background: ${appColor.App.white};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
`;

export const authPageMain = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const authPanel = css`
  width: 100%;
  max-width: 480px;
  margin: auto;
`;

export const authFormContainer = css`
  width: 100%;
`;

export const authFormSections = css`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`;

export const authLabelStyled = css`
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.label};
  line-height: 16px;
  letter-spacing: 0.1px;
  color: ${authColors.body};
`;

export const authInputStyled = css`
  width: 100%;
  & .MuiOutlinedInput-root {
    border-radius: 4px;
    background: ${authColors.inputBg};
    fieldset {
      border-color: ${authColors.inputBorder};
    }
    &:hover fieldset {
      border-color: ${authColors.inputBorder};
    }
    &.Mui-focused fieldset {
      border-color: ${appColor.App.blueMain};
      border-width: 1px;
    }
  }
  & .MuiOutlinedInput-input {
    padding: 14px 16px;
    font-size: ${authFontSizes.label};
    line-height: 20px;
    color: ${authColors.title};
    &::placeholder {
      color: ${authColors.body};
      opacity: 1;
    }
  }
`;

export const authHeaderSection = css`
  text-align: left;
`;

export const authLogoWrapper = css`
  display: flex;
  justify-content: flex-start;
  padding-bottom: 24px;
`;

export const authTitle = css`
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 700;
  font-size: ${authFontSizes.title};
  line-height: 28px;
  letter-spacing: 0.1px;
  color: ${authColors.title};
  text-align: left;
`;

export const authSubtitle = css`
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.subtitle};
  line-height: 20px;
  letter-spacing: 0.1px;
  color: ${authColors.body};
  text-align: left;
`;

export const authSubmitButton = css`
  ${CustomMuiButtonBlue};
  width: 100%;
  height: 48px;
  padding: 0 22px;
  font-size: ${authFontSizes.button};
  font-weight: 700;
  text-transform: none;
  box-shadow: none;
  border-radius: 56px;
  background: ${appColor.App.blueMain};
  &:hover {
    box-shadow: none;
    background: ${appColor.App.blueMain};
    opacity: 0.92;
  }
  &.Mui-disabled {
    background: ${appColor.App.blueMain};
    color: ${appColor.App.white};
    opacity: 0.4;
  }
`;

export const authLinkStyled = css`
  display: flex;
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.link};
  line-height: 20px;
  letter-spacing: 0.1px;
  justify-content: flex-start;
  a {
    color: ${authColors.body};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const authLoginLinkStyled = css`
  display: flex;
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.link};
  line-height: 20px;
  letter-spacing: 0.1px;
  color: ${authColors.body};
  justify-content: flex-start;
  a {
    font-weight: 700;
    color: ${authColors.body};
    margin-left: 4px;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const authFooterStyled = css`
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.footer};
  line-height: 16px;
  letter-spacing: 0.1px;
  color: ${authColors.body};
  a {
    color: ${authColors.body};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const authFooterDividerStyled = css`
  margin: 0 10px;
  color: ${authColors.body};
`;

/** 区切り線と後続リンクをひとまとまりに保ち、折り返しで区切り線が行頭に残らないようにする */
export const authFooterItemStyled = css`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

/** フッター下部のロゴ（#1265 添付デザイン準拠 / #1433） */
export const authFooterLogoStyled = css`
  margin-top: 16px;
  gap: 8px;

  svg {
    width: 64px;
    height: auto;
    opacity: 0.45;
  }
`;

export const authFooterCopyrightStyled = css`
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: ${authFontSizes.footer};
  line-height: 16px;
  letter-spacing: 0.1px;
  color: ${authColors.body};
`;
